import { type ReactNode, useEffect, useRef } from 'react';
import { getPosition } from '../lib/device.js';
import { fmtDT } from '../lib/format.js';
import { SIGN_VIA_LABEL } from '../lib/status.js';
import { Icon } from './Icon.js';
import { useModal } from './Modal.js';

// Tanda tangan digital toko — port A.sigPad / A.sigPadHtml / A.signBlock / A.signModal v1 (core.js).

export interface SigPadApi {
  empty: () => boolean;
  png: () => string;
  clear: () => void;
}

const MIN = 12; // gerakan minimal agar dianggap tanda tangan

/** Kanvas tanda tangan (jari/stylus/mouse). padRef diisi API { empty, png, clear }. */
export function SignaturePad({ padRef }: { padRef: { current: SigPadApi | null } }) {
  const wrap = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cv = canvas.current;
    const wr = wrap.current;
    const ctx = cv?.getContext('2d');
    if (!cv || !wr || !ctx) return;
    let pts = 0;
    let drawing = false;
    let last: { x: number; y: number } | null = null;
    let w = 0;
    const fit = () => {
      const cw = cv.clientWidth;
      const ch = cv.clientHeight;
      if (!cw || !ch) return; // masih tersembunyi; diukur lagi saat tampil (ResizeObserver)
      w = cw;
      const dpr = Math.min(3, window.devicePixelRatio || 1);
      cv.width = Math.round(cw * dpr);
      cv.height = Math.round(ch * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, cw, ch);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#1d2a6b';
      ctx.lineWidth = 2.6;
      pts = 0;
      wr.classList.remove('has');
    };
    const pos = (e: PointerEvent) => {
      const r = cv.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const down = (e: PointerEvent) => {
      e.preventDefault();
      cv.setPointerCapture(e.pointerId);
      drawing = true;
      last = pos(e);
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
    };
    const move = (e: PointerEvent) => {
      if (!drawing || !last) return;
      const p = pos(e);
      const mid = { x: (last.x + p.x) / 2, y: (last.y + p.y) / 2 };
      ctx.quadraticCurveTo(last.x, last.y, mid.x, mid.y);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(mid.x, mid.y);
      last = p;
      pts += 1;
      if (pts >= MIN) wr.classList.add('has');
    };
    const end = () => {
      drawing = false;
    };
    cv.addEventListener('pointerdown', down);
    cv.addEventListener('pointermove', move);
    cv.addEventListener('pointerup', end);
    cv.addEventListener('pointercancel', end);
    const raf = requestAnimationFrame(fit);
    // Ukuran berubah (HP diputar / panel ditampilkan): kanvas diukur ulang & dikosongkan agar goresan tidak melar.
    const ro = window.ResizeObserver
      ? new ResizeObserver(() => {
          if (cv.clientWidth && cv.clientWidth !== w) fit();
        })
      : null;
    ro?.observe(cv);
    padRef.current = { empty: () => pts < MIN, png: () => cv.toDataURL('image/png'), clear: fit };
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      cv.removeEventListener('pointerdown', down);
      cv.removeEventListener('pointermove', move);
      cv.removeEventListener('pointerup', end);
      cv.removeEventListener('pointercancel', end);
      padRef.current = null;
    };
  }, [padRef]);

  return (
    <div className="sig-wrap" ref={wrap}>
      <canvas ref={canvas} aria-label="Area tanda tangan" />
      <span className="sig-hint">Tanda tangan di sini</span>
      <span className="sig-line" />
      <button type="button" className="sig-clear" data-sig-clear onClick={() => padRef.current?.clear()}>
        Hapus
      </button>
    </div>
  );
}

export interface SignedFields {
  signed_at?: string | null;
  signed_name?: string;
  sign_file?: string | null;
  sign_via?: string;
  sign_hash?: string;
  sign_dist?: number | null;
}

/** Blok bukti persetujuan toko (detail SPO / pesanan). */
export function SignBlock({ x }: { x: SignedFields }) {
  if (!x.signed_at) {
    return (
      <div className="sign-box pending">
        <Icon n="alert" cls="i-sm" />
        <div>
          <b>Belum disetujui toko</b>
          <span>Order perlu tanda tangan digital toko sebelum bisa diverifikasi admin.</span>
        </div>
      </div>
    );
  }
  return (
    <div className="sign-box">
      {x.sign_file ? (
        <img src={`/api/files/${x.sign_file}`} alt={`Tanda tangan ${x.signed_name ?? ''}`} />
      ) : (
        <span className="sign-ok">
          <Icon n="check" />
        </span>
      )}
      <div>
        <b>{`Disetujui toko: ${x.signed_name}`}</b>
        <span>
          {`${fmtDT(x.signed_at)} · ${SIGN_VIA_LABEL[x.sign_via ?? ''] || x.sign_via}${
            x.sign_via === 'sales' && 'sign_dist' in x ? (x.sign_dist == null ? ' · ' : ` · ±${x.sign_dist} m dari toko`) : ''
          }`}
          {x.sign_via === 'sales' && 'sign_dist' in x && x.sign_dist == null ? (
            <span className="warn-txt">lokasi tidak tercatat</span>
          ) : null}
        </span>
        {x.sign_hash ? <span className="mono small">{`Kode verifikasi ${x.sign_hash}`}</span> : null}
      </div>
    </div>
  );
}

export interface SignBody {
  signer_name: string;
  signature: string;
  lat?: number;
  lng?: number;
}

/** Modal tanda tangan: summary = ringkasan order; submit(body) mengirim ke server. */
export function useSignModal() {
  const { open } = useModal();
  return ({
    title,
    summary,
    name = '',
    submit,
  }: {
    title: ReactNode;
    summary: ReactNode;
    name?: string;
    submit: (b: SignBody) => Promise<void>;
  }) => {
    const pad: { current: SigPadApi | null } = { current: null };
    return open({
      title,
      body: (
        <>
          {summary}
          <label className="f">
            Nama penanda tangan (pemilik / penanggung jawab toko)
            <input autoComplete="off" className="inp" name="signer" maxLength={80} defaultValue={name} required />
          </label>
          <SignaturePad padRef={pad} />
          <div className="small muted">
            Dengan menandatangani, toko menyetujui pesanan ini (produk, jumlah, harga & cara bayar). Waktu, lokasi & kode
            verifikasi tercatat.
          </div>
        </>
      ),
      foot: (m) => (
        <>
          <button type="button" className="btn" data-m="close" onClick={m.close}>
            Batal
          </button>
          <button type="submit" className="btn btn-primary">
            <Icon n="check" cls="i-sm" /> Setujui &amp; Tanda Tangan
          </button>
        </>
      ),
      submit: async (f, m) => {
        const signer = (f.elements.namedItem('signer') as HTMLInputElement).value.trim();
        if (!signer) throw new Error('Isi nama penanda tangan.');
        if (!pad.current || pad.current.empty()) throw new Error('Tanda tangan belum dibuat / terlalu pendek.');
        const p = await getPosition(4000);
        await submit({ signer_name: signer, signature: pad.current.png(), lat: p?.lat, lng: p?.lng });
        m.close();
      },
    });
  };
}
